
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../types';

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product, startRect?: DOMRect) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart }) => {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);

  const displayImage = (!product.image || imgError) 
    ? `https://placehold.co/600x600/f1f5f9/6366f1?text=${encodeURIComponent(product.name)}`
    : product.image;

  const hasDiscount = product.originalPrice && product.originalPrice > product.price;
  const discountRate = hasDiscount ? Math.round(((product.originalPrice! - product.price) / product.originalPrice!) * 100) : 0;

  const handleAction = (e: React.MouseEvent | React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation();
    }

    if (e.type === 'click') {
      if ((product.options?.length ?? 0) > 0 || (product.addOns?.length ?? 0) > 0) {
        navigate(`/product/${product.id}`);
      } else {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        onAddToCart(product, rect);
      }
    }
  };

  return (
    <div 
      onClick={() => navigate(`/product/${product.id}`)}
      className={`group relative z-10 flex flex-col bg-white rounded-[32px] border ${hasDiscount ? 'border-indigo-50 shadow-indigo-50' : 'border-gray-100'} overflow-hidden hover:shadow-2xl transition-all duration-500 cursor-pointer`}
    >
      <div className="relative aspect-square overflow-hidden bg-gray-50">
        <img 
          src={displayImage} 
          alt={product.name} 
          loading="lazy"
          onError={() => setImgError(true)}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {product.isNew && (
            <span className="px-3 py-1 bg-gray-900 text-white text-[10px] font-black rounded-full shadow-lg uppercase tracking-widest">
              NEW
            </span>
          )}
          {hasDiscount && (
            <div className="flex flex-col gap-1">
              {/* [Feature 4] 할인 뱃지 색상 변경: Red -> Blue/Indigo */}
              <span className="px-3 py-1 bg-gradient-to-r from-blue-600 to-indigo-500 text-white text-[10px] font-black rounded-full shadow-xl uppercase tracking-widest flex items-center gap-1 animate-pulse">
                <span className="text-[8px]">⚡</span> {discountRate}% OFF
              </span>
              <span className="px-2 py-0.5 bg-black/60 backdrop-blur-md text-white text-[7px] font-black rounded-md w-fit uppercase">Sale</span>
            </div>
          )}
        </div>
        {product.stock <= 0 && (
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center">
            <span className="px-4 py-2 bg-white/90 text-gray-900 font-black rounded-xl text-sm italic tracking-tighter uppercase">Sold Out</span>
          </div>
        )}
      </div>
      
      <div className="p-6 flex flex-col flex-1">
        <div className="flex flex-wrap gap-2 mb-3">
           {product.categories && product.categories.length > 0 ? (
             product.categories.slice(0, 1).map(cat => (
               <span key={cat} className="text-[10px] font-black text-indigo-600 bg-indigo-50 px-2 py-1 rounded-lg uppercase tracking-tighter">
                 {cat}
               </span>
             ))
           ) : (
             <span className="text-[10px] font-black text-gray-400 bg-gray-50 px-2 py-1 rounded-lg uppercase tracking-tighter">
               General
             </span>
           )}
        </div>
        <h3 className="text-sm font-black text-gray-900 mb-2 line-clamp-2 min-h-[40px] leading-tight group-hover:text-indigo-600 transition-colors">
          {product.name}
        </h3>
        
        <div className="mt-auto pt-4 border-t border-gray-50 flex items-end justify-between">
          <div className="flex flex-col">
            {hasDiscount && (
              <span className="text-[11px] font-bold text-gray-400 line-through">
                {product.originalPrice?.toLocaleString()}원
              </span>
            )}
            {/* [Feature 4] 가격 텍스트 색상 변경: Rose -> Indigo/Blue */}
            <span className={`text-lg font-black leading-none ${hasDiscount ? 'text-indigo-600' : 'text-gray-900'}`}>
              {product.price.toLocaleString()}원
            </span>
          </div>
          <button 
            type="button"
            onClick={handleAction}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={product.stock <= 0}
            // [Feature 4] 버튼 색상도 파란 계열(Indigo/Blue)로 통일
            className={`relative z-20 p-3 rounded-2xl transition shadow-lg active:scale-90 disabled:bg-gray-200 disabled:text-gray-400 ${hasDiscount ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-900 text-white hover:bg-indigo-600'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-6 h-6 pointer-events-none">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
